/**
 * Sincronización de la selección con los bloqueos del servidor. Sin DOM ni `fetch`: la red entra por `HoldApi`.
 *
 * Modelo: `desired` es lo que el comprador quiere (se actualiza al instante, de forma optimista) y `confirmed`
 * lo que el servidor ha aceptado. Un único bucle de reconciliación, serializado en una cola, calcula la
 * diferencia y la envía: quita primero, añade después. Consecuencias:
 *   - nunca hay dos peticiones de bloqueo en vuelo;
 *   - clics rápidos se agrupan (A, B, quitar A -> una sola petición con B);
 *   - si el servidor rechaza algo, se revierte solo eso (rollback) y el resto sigue su curso;
 *   - `quota_exceeded` / `session_*` no se reintentan nunca; `session_*` además cierran el paso a bloqueos NUEVOS
 *     (`lock`) hasta que el portal renueve la sesión: ni bucles ni peticiones que se sabe que fallarán;
 *   - cuando no queda nada seleccionado se libera el bloqueo, y el siguiente clic abre uno nuevo con tiempo completo.
 */
import { ApiError, FINAL_CODES } from "./api.js";
import { SerialQueue } from "./queue.js";
import type { GaReq, HoldApi, HoldView, PlaceRequest } from "./types.js";

export interface HoldState {
  token: string;
  expiresAt: number;
  createdAt: number;
}

export type SyncOp = "place" | "remove" | "release" | "extend" | "best" | "get";

export interface SyncHandlers {
  /** cambió lo que el comprador tiene seleccionado (también tras un rollback) */
  selectionChanged(): void;
  /** el bloqueo se creó, se amplió, se extendió o desapareció (null) */
  holdChanged(hold: HoldState | null): void;
  /** hora del servidor observada en una respuesta */
  serverTime(ts: number): void;
  /** el servidor confirmó que estas butacas ya no son mías */
  released(ids: string[], serverTime: number): void;
  /** butacas que otro comprador se llevó antes que yo */
  conflict(ids: string[]): void;
  /** la zona de pie ya no tiene tantas entradas */
  capacity(zoneId: string): void;
  /** el bloqueo dejó de existir (venció) o se convirtió en compra */
  gone(reason: "expired" | "booked"): void;
  error(err: ApiError, op: SyncOp): void;
  /** cambió el estado "hay cambios sin confirmar" */
  pending(pending: boolean): void;
}

export interface SyncOptions {
  sleep?: (ms: number) => Promise<void>;
  /** reintentos automáticos ante `busy` */
  retries?: number;
  /** sin servidor: la selección es solo local */
  localOnly?: boolean;
}

const GONE_CODES = new Set(["hold_expired", "hold_not_active", "hold_lost"]);

export const toApiError = (e: unknown): ApiError => (e instanceof ApiError ? e : new ApiError("unknown", e instanceof Error ? e.message : String(e)));

type Step = "ok" | "retry" | "stop";

export class HoldSync {
  readonly queue = new SerialQueue();
  readonly desiredSeats = new Set<string>();
  readonly desiredGa = new Map<string, number>();
  confirmedSeats = new Set<string>();
  confirmedGa = new Map<string, number>();
  hold: HoldState | null = null;
  /**
   * Sesión ausente o caducada: mientras haya `lock` y no exista un bloqueo (los bloqueos que ya existen se amplían,
   * quitan y extienden sin sesión) no se envía ninguna petición de bloqueo y cada intento se avisa con `error`.
   */
  lock: ApiError | null = null;

  private reconcileQueued = false;
  private dead = false;
  private lastPending = false;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly retries: number;
  readonly local: boolean;

  constructor(
    private readonly api: HoldApi,
    private readonly h: SyncHandlers,
    opts: SyncOptions = {},
  ) {
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.retries = opts.retries ?? 2;
    this.local = opts.localOnly === true;
  }

  /* ------------------------------ consulta -------------------------------- */

  hasSeat = (id: string): boolean => this.desiredSeats.has(id);
  gaQty = (zoneId: string): number => this.desiredGa.get(zoneId) ?? 0;
  confirmedGaQty = (zoneId: string): number => this.confirmedGa.get(zoneId) ?? 0;

  count(): number {
    let n = this.desiredSeats.size;
    for (const q of this.desiredGa.values()) n += q;
    return n;
  }

  isPending(): boolean {
    return this.queue.pending > 0 || this.hasDiff();
  }

  private hasDiff(): boolean {
    for (const id of this.desiredSeats) if (!this.confirmedSeats.has(id)) return true;
    for (const id of this.confirmedSeats) if (!this.desiredSeats.has(id)) return true;
    const zones = new Set([...this.desiredGa.keys(), ...this.confirmedGa.keys()]);
    for (const z of zones) if ((this.desiredGa.get(z) ?? 0) !== (this.confirmedGa.get(z) ?? 0)) return true;
    return false;
  }

  /* ------------------------------ acciones -------------------------------- */

  /** Devuelve false si el intento se rechazó sin tocar nada (sesión bloqueada): `error` ya avisó. */
  private refused(): boolean {
    if (this.local || !this.lock || this.hold) return false;
    this.h.error(this.lock, "place");
    return true;
  }

  addSeat(id: string): boolean {
    if (this.desiredSeats.has(id)) return true;
    if (this.refused()) return false;
    this.desiredSeats.add(id);
    this.h.selectionChanged();
    this.schedule();
    return true;
  }

  removeSeat(id: string): void {
    if (!this.desiredSeats.delete(id)) return;
    this.h.selectionChanged();
    this.schedule();
  }

  setGa(zoneId: string, qty: number): boolean {
    const q = Math.max(0, Math.floor(qty));
    const cur = this.desiredGa.get(zoneId) ?? 0;
    if (cur === q) return true;
    if (q > cur && this.refused()) return false;
    if (q === 0) this.desiredGa.delete(zoneId);
    else this.desiredGa.set(zoneId, q);
    this.h.selectionChanged();
    this.schedule();
    return true;
  }

  /** Vacía la selección y libera el bloqueo en el servidor. */
  clear(): Promise<void> {
    this.desiredSeats.clear();
    this.desiredGa.clear();
    this.h.selectionChanged();
    this.schedule();
    return this.queue.idle();
  }

  /** Avisa de `pending` al empezar y, sobre todo, cuando la tarea ya ha salido de la cola. */
  private track<T>(p: Promise<T>): Promise<T> {
    this.emitPending();
    return p.finally(() => this.emitPending());
  }

  /** Se resuelve cuando todo lo encolado hasta ahora ha terminado. */
  flush(): Promise<void> {
    return this.queue.idle();
  }

  destroy(): void {
    this.dead = true;
  }

  /** El reloj dice que el bloqueo ya venció: se limpia sin esperar al servidor. */
  expireLocal(): void {
    if (this.hold) this.goneInternal("expired");
  }

  extend(): Promise<{ hold: HoldState; extended: boolean }> {
    return this.track(this.queue.push(async () => {
      if (!this.hold) throw new ApiError("no_hold", "No hay ningún bloqueo activo", 0);
      const before = this.hold.expiresAt;
      try {
        const view = await this.retrying(() => this.api.extend(this.hold!.token));
        this.applyView(view);
        return { hold: this.hold!, extended: view.expiresAt > before };
      } catch (e) {
        const err = toApiError(e);
        if (this.isGone(err)) this.goneInternal("expired");
        throw err;
      }
    }));
  }

  /**
   * Adopta un bloqueo que ya existía (recuperado tras recargar la página) como si este widget lo hubiera creado: a
   * partir de aquí ampliar, quitar, extender y soltar funcionan igual. Solo antes de que el comprador toque nada.
   */
  restore(view: HoldView): void {
    for (const id of view.seats ?? []) this.desiredSeats.add(id);
    for (const g of view.ga ?? []) if (g.qty > 0) this.desiredGa.set(g.zoneId, g.qty);
    this.applyView(view);
    this.h.selectionChanged();
  }

  /** Pide al servidor las mejores butacas contiguas y las añade a la selección. */
  selectBest(count: number, category?: string): Promise<string[]> {
    return this.track(this.queue.push(async () => {
      if (this.lock && !this.hold && !this.local) throw this.lock; // sin sesión no hay petición que valga; avisa quien llama
      const req: PlaceRequest = {
        ...(this.hold ? { holdToken: this.hold.token } : {}),
        bestAvailable: { count, ...(category ? { category } : {}) },
      };
      try {
        const view = await this.retrying(() => this.api.place(req));
        const before = new Set(this.confirmedSeats);
        this.applyView(view);
        const added = view.seats.filter((id) => !before.has(id));
        for (const id of added) this.desiredSeats.add(id);
        this.h.selectionChanged();
        return added;
      } catch (e) {
        const err = toApiError(e);
        if (this.isGone(err)) this.goneInternal("expired");
        else this.noteSession(err);
        throw err;
      }
    }));
  }

  /**
   * Alguna butaca confirmada aparece vendida en la foto. Se pregunta al servidor: si el bloqueo ya es una
   * compra, la selección se cierra sin avisos de "butaca perdida"; si no, se descartan las que ya no son mías.
   */
  verify(candidates: string[]): Promise<void> {
    return this.track(this.queue.push(async () => {
      if (!this.hold || this.dead) return;
      try {
        const v = await this.api.get(this.hold.token);
        if (v.status === "booked") return this.goneInternal("booked");
        if (v.status && v.status !== "held") return this.goneInternal("expired");
        const lost = candidates.filter((id) => !v.seats.includes(id));
        if (lost.length === 0) return;
        for (const id of lost) this.desiredSeats.delete(id);
        this.applyView(v);
        this.h.selectionChanged();
        this.h.conflict(lost);
      } catch (e) {
        if (this.isGone(toApiError(e))) this.goneInternal("expired");
      }
    }));
  }

  /* ------------------------------ bucle ----------------------------------- */

  private schedule(): void {
    if (this.local) {
      this.confirmedSeats = new Set(this.desiredSeats);
      this.confirmedGa = new Map(this.desiredGa);
      this.emitPending();
      return;
    }
    if (!this.reconcileQueued) {
      this.reconcileQueued = true;
      this.queue
        .push(async () => {
          this.reconcileQueued = false;
          await this.reconcile();
        })
        .catch(() => undefined)
        .finally(() => this.emitPending());
    }
    this.emitPending();
  }

  private emitPending(): void {
    const p = this.isPending();
    if (p !== this.lastPending) {
      this.lastPending = p;
      this.h.pending(p);
    }
  }

  private gaDiff(): GaReq[] {
    const out: GaReq[] = [];
    const zones = new Set([...this.desiredGa.keys(), ...this.confirmedGa.keys()]);
    for (const zoneId of zones) {
      const want = this.desiredGa.get(zoneId) ?? 0;
      if (want !== (this.confirmedGa.get(zoneId) ?? 0)) out.push({ zoneId, qty: want });
    }
    return out;
  }

  private async reconcile(): Promise<void> {
    for (let guard = 0; guard < 25 && !this.dead; guard++) {
      const adds = [...this.desiredSeats].filter((id) => !this.confirmedSeats.has(id));
      const removes = [...this.confirmedSeats].filter((id) => !this.desiredSeats.has(id));
      const gaOps = this.gaDiff();
      if (adds.length === 0 && removes.length === 0 && gaOps.length === 0) return;

      if (this.count() === 0) {
        if (this.hold) {
          await this.doRelease();
          continue;
        }
        this.confirmedSeats.clear();
        this.confirmedGa.clear();
        return;
      }
      if (removes.length > 0 && this.hold) {
        if ((await this.doRemove(removes)) === "stop") return;
        continue;
      }
      if (removes.length > 0) this.confirmedSeats = new Set(this.desiredSeats); // sin bloqueo no hay nada que quitar
      if (adds.length === 0 && gaOps.length === 0) continue;
      if ((await this.doPlace(adds, gaOps)) === "stop") return;
    }
  }

  private async doRelease(): Promise<void> {
    const token = this.hold!.token;
    const ids = [...this.confirmedSeats];
    let serverTime = 0;
    try {
      const view = (await this.retrying(() => this.api.release(token))) as Partial<HoldView> | null;
      serverTime = view?.serverTime ?? 0;
    } catch {
      /* Mejor esfuerzo: si falla, el bloqueo vence solo. */
    }
    this.hold = null;
    this.confirmedSeats.clear();
    this.confirmedGa.clear();
    this.h.holdChanged(null);
    if (ids.length) this.h.released(ids, serverTime);
  }

  private async doRemove(removes: string[]): Promise<Step> {
    try {
      const view = await this.retrying(() => this.api.remove(this.hold!.token, removes));
      this.applyView(view);
      this.h.released(removes, view.serverTime ?? 0);
      return "ok";
    } catch (e) {
      return this.fail(e, "remove", { adds: [], removes, gaOps: [] });
    }
  }

  private async doPlace(adds: string[], gaOps: GaReq[]): Promise<Step> {
    const req: PlaceRequest = {
      ...(this.hold ? { holdToken: this.hold.token } : {}),
      ...(adds.length ? { seats: adds } : {}),
      ...(gaOps.length ? { ga: gaOps } : {}),
    };
    try {
      const view = await this.retrying(() => this.api.place(req));
      this.applyView(view);
      return "ok";
    } catch (e) {
      return this.fail(e, "place", { adds, removes: [], gaOps });
    }
  }

  private applyView(view: HoldView): void {
    this.confirmedSeats = new Set(view.seats ?? []);
    this.confirmedGa = new Map((view.ga ?? []).map((g) => [g.zoneId, g.qty] as const));
    this.hold = { token: view.holdToken, expiresAt: view.expiresAt, createdAt: view.createdAt ?? this.hold?.createdAt ?? 0 };
    if (typeof view.serverTime === "number") this.h.serverTime(view.serverTime);
    this.h.holdChanged(this.hold);
  }

  /** Una sesión rechazada por el servidor cierra el paso a bloqueos nuevos hasta que el portal la renueve. */
  private noteSession(err: ApiError): void {
    if ((err.code === "session_required" || err.code === "session_invalid") && !this.hold) this.lock = err;
  }

  private isGone(err: ApiError): boolean {
    return GONE_CODES.has(err.code) || (err.code === "not_found" && this.hold !== null);
  }

  private goneInternal(reason: "expired" | "booked"): void {
    this.desiredSeats.clear();
    this.desiredGa.clear();
    this.confirmedSeats.clear();
    this.confirmedGa.clear();
    this.hold = null;
    this.h.holdChanged(null);
    this.h.selectionChanged();
    this.h.gone(reason);
  }

  private fail(e: unknown, op: SyncOp, ctx: { adds: string[]; removes: string[]; gaOps: GaReq[] }): Step {
    const err = toApiError(e);
    if (err.code === "aborted" || this.dead) return "stop";

    if (this.isGone(err)) {
      this.goneInternal("expired");
      return "stop";
    }

    if (err.code === "seat_unavailable" && op === "place") {
      const listed = (err.details as { seats?: unknown } | undefined)?.seats;
      const ids = Array.isArray(listed) ? (listed as string[]) : [];
      const hit = ctx.adds.filter((id) => ids.includes(id));
      const failed = hit.length ? hit : ctx.adds; // el servidor es todo-o-nada: sin detalle, se asume que fallaron todas
      for (const id of failed) this.desiredSeats.delete(id);
      this.h.selectionChanged();
      this.h.conflict(failed);
      return failed.length || ctx.gaOps.length ? "retry" : "stop";
    }

    if (err.code === "capacity_exceeded" && op === "place") {
      const zone = (err.details as { zone?: unknown } | undefined)?.zone;
      const zones = typeof zone === "string" ? [zone] : ctx.gaOps.map((g) => g.zoneId);
      for (const z of zones) {
        const have = this.confirmedGa.get(z) ?? 0;
        if (have > 0) this.desiredGa.set(z, have);
        else this.desiredGa.delete(z);
        this.h.capacity(z);
      }
      this.h.selectionChanged();
      return "retry";
    }

    // Cuota o sesión: rollback de lo que esta petición intentaba (abajo) y aviso, pero NO se marcan butacas como ocupadas
    // (no lo están) ni se reintenta: la cuota solo se libera soltando o vencimiento, y la sesión la renueva el portal.
    this.noteSession(err);

    // Cualquier otro fallo: se deshace lo que esta petición intentaba y se avisa.
    for (const id of ctx.adds) this.desiredSeats.delete(id);
    for (const id of ctx.removes) this.desiredSeats.add(id);
    for (const g of ctx.gaOps) {
      const have = this.confirmedGa.get(g.zoneId) ?? 0;
      if (have > 0) this.desiredGa.set(g.zoneId, have);
      else this.desiredGa.delete(g.zoneId);
    }
    this.h.selectionChanged();
    this.h.error(err, op);
    return "stop";
  }

  /** Reintenta solo cuando el servidor dice que hay demasiada concurrencia; un fallo de red no se repite (crearía bloqueos duplicados). */
  private async retrying<T>(fn: () => Promise<T>): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await fn();
      } catch (e) {
        const err = toApiError(e);
        const transient = !FINAL_CODES.has(err.code) && (err.code === "busy" || err.code === "conflict" || (err.details as { retryable?: boolean } | undefined)?.retryable === true);
        if (!transient || attempt >= this.retries || this.dead) throw err;
        await this.sleep(250 * (attempt + 1));
      }
    }
  }
}
